import React, { useState, useEffect } from 'react';
import { TrendingUp, TrendingDown, Award, Target, Calendar, Hash, Coins } from 'lucide-react';
import { BACKEND_URL } from '../../services/config';

/**
 * PerformanceDashboard v11.7 — HONEST win rate from auto_signal_alerts only.
 * Fixed misleading 63.4% number; now shows true broadcast win rate (~45%).
 * Added: Net pips, per-pair breakdown, per-timeframe breakdown.
 */
const PerformanceDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('fira_auth_token');
      const headers = { Authorization: `Bearer ${token}` };
      // Both endpoints now default to source=broadcast (auto_signal_alerts only)
      const [perfRes, dailyRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/premium/signals/performance`, { headers }),
        fetch(`${BACKEND_URL}/api/admin/signals/report/daily`, { headers }),
      ]);
      const perf = await perfRes.json();
      const daily = await dailyRes.json();
      setData({ performance: perf, daily: daily.summary || daily });
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const StatCard = ({ icon: Icon, label, value, color, sub }) => (
    <div style={{ background: '#FFF', borderRadius: '16px', padding: '16px', border: '1px solid #E2E8F0' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
        <div style={{ width: '32px', height: '32px', borderRadius: '10px', background: `${color}12`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Icon size={16} color={color} />
        </div>
        <span style={{ fontSize: '11px', fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.1em' }}>{label}</span>
      </div>
      <div style={{ fontSize: '24px', fontWeight: 700, color: '#0F172A', fontFamily: 'Poppins, sans-serif' }}>{value}</div>
      {sub && <div style={{ fontSize: '12px', color: '#94A3B8', marginTop: '2px' }}>{sub}</div>}
    </div>
  );

  const perf = data?.performance || {};
  const daily = data?.daily || {};

  const pairBreakdown = perf.win_rate_by_pair || {};
  const tfBreakdown = perf.win_rate_by_timeframe || {};
  const netPips = perf.net_pips || 0;

  return (
    <div data-testid="performance-dashboard" style={{ fontFamily: 'Inter, sans-serif', maxWidth: '480px', margin: '0 auto' }}>
      <h1 style={{ fontSize: '24px', fontWeight: 700, color: '#0F172A', fontFamily: 'Poppins, sans-serif', marginBottom: '4px' }}>Performance</h1>
      <p style={{ fontSize: '13px', color: '#94A3B8', marginBottom: '20px' }}>
        Live stats from all broadcast signals
      </p>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <div style={{ width: '36px', height: '36px', borderRadius: '50%', border: '3px solid #F1F5F9', borderTopColor: '#F5A623', animation: 'spin 0.8s linear infinite', margin: '0 auto' }} />
        </div>
      ) : (
        <>
          {/* Win Rate Hero */}
          <div style={{
            background: 'linear-gradient(135deg, #F5A623, #E8951A)', borderRadius: '20px', padding: '24px',
            color: '#FFF', marginBottom: '16px', textAlign: 'center', boxShadow: '0 8px 24px rgba(245,166,35,0.25)',
          }}>
            <div style={{ fontSize: '11px', fontWeight: 700, opacity: 0.85, marginBottom: '4px', letterSpacing: '0.15em' }}>
              BROADCAST WIN RATE
            </div>
            <div data-testid="perf-win-rate" style={{ fontSize: '48px', fontWeight: 800, fontFamily: 'Poppins', lineHeight: 1 }}>
              {perf.win_rate != null ? perf.win_rate : 0}%
            </div>
            <div style={{ fontSize: '13px', opacity: 0.92, marginTop: '6px' }}>
              {perf.wins || 0} wins / {perf.losses || 0} losses
              {perf.completed > 0 && ` • ${perf.completed} completed`}
            </div>
            <div style={{
              display: 'inline-block', marginTop: '10px', padding: '4px 10px',
              borderRadius: '999px', background: 'rgba(255,255,255,0.15)',
              fontSize: '11px', fontWeight: 600, letterSpacing: '0.04em',
            }}>
              From {perf.total_signals || 0} signals sent to subscribers
            </div>
          </div>

          {/* Net Pips Card */}
          <div style={{
            background: '#FFF', borderRadius: '16px', padding: '16px',
            border: `1px solid ${netPips >= 0 ? 'rgba(16,185,129,0.25)' : 'rgba(239,68,68,0.25)'}`,
            marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '12px',
          }}>
            <div style={{
              width: '44px', height: '44px', borderRadius: '12px',
              background: netPips >= 0 ? 'rgba(16,185,129,0.10)' : 'rgba(239,68,68,0.10)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Coins size={20} color={netPips >= 0 ? '#10B981' : '#EF4444'} />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '11px', fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                Net Pips (All-Time)
              </div>
              <div data-testid="perf-net-pips" style={{ fontSize: '22px', fontWeight: 800, color: netPips >= 0 ? '#047857' : '#B91C1C', fontFamily: 'Poppins' }}>
                {netPips >= 0 ? '+' : ''}{netPips.toLocaleString()}
              </div>
            </div>
          </div>

          {/* Stats Grid */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
            <StatCard icon={Target} label="Total Signals" value={perf.total_signals || 0} color="#6366F1" />
            <StatCard icon={Award} label="Pending" value={perf.pending || 0} color="#F5A623" />
            <StatCard icon={TrendingUp} label="Wins" value={perf.wins || 0} color="#10B981" />
            <StatCard icon={TrendingDown} label="Losses" value={perf.losses || 0} color="#EF4444" />
          </div>

          {/* Today */}
          <div style={{ background: '#FFF', borderRadius: '20px', padding: '20px', border: '1px solid #E2E8F0', marginBottom: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px' }}>
              <Calendar size={16} color="#F5A623" />
              <span style={{ fontSize: '13px', fontWeight: 700, color: '#0F172A' }}>Today's Report</span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px' }}>
              {[
                { label: 'Signals', value: daily.total_signals || 0, color: '#6366F1' },
                { label: 'Wins', value: daily.wins || 0, color: '#10B981' },
                { label: 'Losses', value: daily.losses || 0, color: '#EF4444' },
              ].map((s, i) => (
                <div key={i} style={{ textAlign: 'center', padding: '12px 8px', background: '#F8FAFC', borderRadius: '12px' }}>
                  <div style={{ fontSize: '20px', fontWeight: 700, color: s.color }}>{s.value}</div>
                  <div style={{ fontSize: '10px', fontWeight: 600, color: '#94A3B8', textTransform: 'uppercase' }}>{s.label}</div>
                </div>
              ))}
            </div>
            {daily.total_pips != null && (
              <div style={{ marginTop: '10px', fontSize: '11px', color: '#64748B', textAlign: 'center' }}>
                Net pips today: <strong style={{ color: daily.total_pips >= 0 ? '#047857' : '#B91C1C' }}>
                  {daily.total_pips >= 0 ? '+' : ''}{daily.total_pips}
                </strong>
              </div>
            )}
          </div>

          {/* Breakdown by Pair */}
          {Object.keys(pairBreakdown).length > 0 && (
            <div style={{ background: '#FFF', borderRadius: '20px', padding: '20px', border: '1px solid #E2E8F0', marginBottom: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px' }}>
                <Hash size={16} color="#8B5CF6" />
                <span style={{ fontSize: '13px', fontWeight: 700, color: '#0F172A' }}>Win Rate by Pair</span>
              </div>
              {Object.entries(pairBreakdown).sort((a, b) => (b[1].total || 0) - (a[1].total || 0)).slice(0, 8).map(([pair, s]) => (
                <div key={pair} data-testid={`perf-pair-${pair.replace('/', '-')}`} style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '10px 0', borderBottom: '1px solid #F1F5F9',
                }}>
                  <div>
                    <div style={{ fontSize: '13px', fontWeight: 700, color: '#0F172A', fontFamily: 'Poppins' }}>{pair}</div>
                    <div style={{ fontSize: '11px', color: '#94A3B8' }}>{s.wins}W / {s.total - s.wins}L • {s.total} completed</div>
                  </div>
                  <div style={{
                    fontSize: '15px', fontWeight: 800,
                    color: s.win_rate >= 60 ? '#10B981' : s.win_rate >= 45 ? '#F5A623' : '#EF4444',
                    fontFamily: 'Poppins',
                  }}>
                    {s.win_rate}%
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Breakdown by Timeframe */}
          {Object.keys(tfBreakdown).length > 0 && (
            <div style={{ background: '#FFF', borderRadius: '20px', padding: '20px', border: '1px solid #E2E8F0', marginBottom: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px' }}>
                <Calendar size={16} color="#0EA5E9" />
                <span style={{ fontSize: '13px', fontWeight: 700, color: '#0F172A' }}>Win Rate by Timeframe</span>
              </div>
              {Object.entries(tfBreakdown).sort((a, b) => (b[1].total || 0) - (a[1].total || 0)).map(([tf, s]) => (
                <div key={tf} style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '10px 0', borderBottom: '1px solid #F1F5F9',
                }}>
                  <div>
                    <div style={{ fontSize: '13px', fontWeight: 700, color: '#0F172A', fontFamily: 'Poppins' }}>{tf}</div>
                    <div style={{ fontSize: '11px', color: '#94A3B8' }}>{s.wins}W / {s.total - s.wins}L</div>
                  </div>
                  <div style={{
                    fontSize: '15px', fontWeight: 800,
                    color: s.win_rate >= 60 ? '#10B981' : s.win_rate >= 45 ? '#F5A623' : '#EF4444',
                    fontFamily: 'Poppins',
                  }}>
                    {s.win_rate}%
                  </div>
                </div>
              ))}
            </div>
          )}

          <div style={{ fontSize: '11px', color: '#94A3B8', textAlign: 'center', padding: '8px', lineHeight: 1.5 }}>
            All stats calculated from actual broadcast signals only.<br />
            <em>Past performance does not guarantee future results.</em>
          </div>
        </>
      )}
    </div>
  );
};

export default PerformanceDashboard;
